import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Clock, Users, Truck, Navigation, Route as RouteIcon, Footprints, AlertTriangle, Loader2, Flag, CheckCircle2, Undo2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navUrl = (r) => {
  if (!r.stops?.length || !r.dest) return null;
  const pts = r.stops.map((s) => `${s.lat},${s.lng}`);
  const origin = pts[0];
  const waypoints = pts.slice(1, 9).join("|");
  return `https://www.google.com/maps/dir/?api=1&origin=${origin}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""}&destination=${r.dest.lat},${r.dest.lng}&travelmode=driving`;
};

export default function DriverTaskPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState({});   // route_id -> bugünkü sefer sayısı
  const [marking, setMarking] = useState(null);
  const [flash, setFlash] = useState(null); // başarı animasyonu için route_id

  useEffect(() => {
    axios.get(`${API}/driver-portal/${token}`)
      .then((res) => {
        setData(res.data);
        setTrips(Object.fromEntries((res.data.routes || []).map(r => [r.id, r.today_trips || 0])));
      })
      .catch((e) => setError(e?.response?.data?.detail || "Görev ekranı yüklenemedi"))
      .finally(() => setLoading(false));
  }, [token]);

  const mark = async (routeId, delta) => {
    setMarking(routeId);
    try {
      const { data: res } = await axios.post(`${API}/driver-portal/${token}/mark-trip`, { route_id: routeId, delta });
      setTrips(t => ({ ...t, [routeId]: res.today_trips }));
      if (delta > 0) { setFlash(routeId); setTimeout(() => setFlash(null), 1600); }
    } catch (e) {
      alert(e?.response?.data?.detail || "İşaretlenemedi — tekrar deneyin");
    } finally {
      setMarking(null);
    }
  };

  const today = new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="text-center" data-testid="driver-task-error">
          <AlertTriangle className="w-10 h-10 text-[#F59E0B] mx-auto mb-4" />
          <div className="text-white font-bold text-lg mb-2">Link Geçersiz</div>
          <div className="text-neutral-400 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  const routes = data.routes || [];

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="driver-task-page">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white px-4 pt-6 pb-5 sticky top-0 z-10">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">Keskintur — Şoför Görev Ekranı</div>
        <div className="text-xl font-bold mt-1" data-testid="driver-name">{data.driver.name}</div>
        <div className="text-xs text-neutral-400 mt-0.5">{today} · <span className="font-semibold text-white">{routes.length}</span> güzergah</div>
      </div>

      <div className="p-3 space-y-3 max-w-lg mx-auto pb-10">
        {routes.length === 0 && (
          <div className="bg-white border border-[#E5E5E5] rounded-md p-10 text-center" data-testid="driver-no-routes">
            <MapPin className="w-8 h-8 mx-auto text-neutral-300 mb-3" />
            <div className="text-sm text-neutral-500">Size atanmış güzergah bulunmuyor.</div>
          </div>
        )}

        {routes.map((r) => {
          const nav = navUrl(r);
          const dirLabel = r.direction === "pickup" ? "ALIM" : "BIRAKMA";
          return (
            <div key={r.id} className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden" data-testid={`driver-route-card-${r.id}`}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white ${r.direction === "pickup" ? "bg-[#16A34A]" : "bg-[#0020C2]"}`}>{dirLabel}</span>
                  {r.shift && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-neutral-100">{r.shift}</span>}
                  {r.vehicle_plate && (
                    <span className="flex items-center gap-1 text-[10px] font-bold ml-auto">
                      <Truck className="w-3 h-3 text-neutral-400" /> {r.vehicle_plate}
                    </span>
                  )}
                </div>
                <div className="font-bold text-sm leading-snug">{r.name}</div>
                <div className="flex items-center gap-3 mt-2 text-xs text-neutral-600 flex-wrap">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-neutral-400" /><b className="num">{r.personnel_count}</b> kişi</span>
                  {r.estimated_km > 0 && <span className="flex items-center gap-1"><RouteIcon className="w-3.5 h-3.5 text-[#0020C2]" /><b className="num">{r.estimated_km}</b> km</span>}
                  {r.duration_min > 0 && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#0020C2]" />~<b className="num">{r.duration_min}</b> dk</span>}
                  {r.stops.length > 0 && <span><b className="num">{r.stops.length}</b> durak</span>}
                </div>
              </div>

              {/* Duraklar */}
              <div className="border-t border-[#F5F5F5] px-4 py-3 space-y-3">
                {r.stops.length > 0 ? (
                  r.stops.map((s, si) => (
                    <div key={si}>
                      <div className="flex items-center gap-2 text-xs font-bold">
                        <span className="w-5 h-5 bg-[#0020C2] text-white rounded-full flex items-center justify-center text-[10px] num shrink-0">{si + 1}</span>
                        Durak {si + 1} {s.pickup_time && <span className="num text-[#0020C2]">{s.pickup_time}</span>} <span className="text-neutral-400 font-normal">· {s.count} kişi</span>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`}
                          target="_blank" rel="noreferrer"
                          className="ml-auto text-[10px] uppercase tracking-widest font-bold text-[#0020C2]"
                        >Haritada Aç</a>
                      </div>
                      {(s.personnel || []).map((p, pi) => (
                        <div key={pi} className="pl-7 py-1">
                          <div className="flex items-center justify-between gap-2 text-xs">
                            <span className="font-semibold">{p.name}</span>
                            <span className="flex items-center gap-2 shrink-0 text-neutral-500">
                              {p.pickup_time && <span className="num font-bold text-[#0A0A0A]">{p.pickup_time}</span>}
                              {p.walk_m > 0 && <span className="flex items-center gap-0.5 num text-[11px]"><Footprints className="w-3 h-3" />{p.walk_m} m</span>}
                            </span>
                          </div>
                          {p.address && <div className="text-[11px] text-neutral-500 leading-snug">{p.address}</div>}
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  (r.personnel || []).map((p, pi) => (
                    <div key={pi} className="flex items-start gap-2 text-xs py-0.5">
                      <span className="text-neutral-400 num w-5 shrink-0">{pi + 1}.</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold">{p.name}</div>
                        {p.address && <div className="text-[11px] text-neutral-500 leading-snug">{p.address}</div>}
                      </div>
                      {p.pickup_time && <span className="num font-bold shrink-0">{p.pickup_time}</span>}
                    </div>
                  ))
                )}

                {(r.dest?.label || r.end_point) && (
                  <div className="flex items-start gap-2 text-xs pt-2 border-t border-[#F5F5F5]">
                    <Flag className="w-3.5 h-3.5 text-[#DC2626] shrink-0 mt-0.5" />
                    <span><span className="font-bold">Varış:</span> {r.dest?.label || r.end_point}</span>
                  </div>
                )}
                {r.notes && <div className="text-[11px] text-neutral-500 italic">Not: {r.notes}</div>}
              </div>

              {/* Sefer işaretleme — puantaja otomatik işlenir */}
              <div className="border-t border-[#F5F5F5] px-4 py-3">
                <button
                  data-testid={`driver-mark-trip-${r.id}`}
                  onClick={() => mark(r.id, 1)}
                  disabled={marking === r.id}
                  className={`w-full flex items-center justify-center gap-2 py-3.5 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors ${
                    flash === r.id ? "bg-[#0A0A0A]" : "bg-[#16A34A] active:bg-[#0A0A0A]"
                  } disabled:opacity-60`}
                >
                  {marking === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {flash === r.id ? "İşaretlendi ✓" : "Bugünkü Seferi İşaretle (+1)"}
                </button>
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span data-testid={`driver-trip-count-${r.id}`} className={trips[r.id] > 0 ? "font-bold text-[#16A34A]" : "text-neutral-400"}>
                    Bugün: <b className="num">{trips[r.id] || 0}</b> sefer işaretlendi
                  </span>
                  {trips[r.id] > 0 && (
                    <button
                      data-testid={`driver-undo-trip-${r.id}`}
                      onClick={() => mark(r.id, -1)}
                      disabled={marking === r.id}
                      className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#DC2626] disabled:opacity-50"
                    >
                      <Undo2 className="w-3 h-3" /> Geri Al
                    </button>
                  )}
                </div>
              </div>

              {nav && (
                <a
                  href={nav} target="_blank" rel="noreferrer"
                  data-testid={`driver-nav-btn-${r.id}`}
                  className="flex items-center justify-center gap-2 bg-[#0020C2] text-white py-3.5 text-xs font-bold uppercase tracking-[0.2em] active:bg-[#0A0A0A]"
                >
                  <Navigation className="w-4 h-4" /> Navigasyonu Başlat
                </a>
              )}
            </div>
          );
        })}

        <div className="text-center text-[10px] text-neutral-400 uppercase tracking-widest pt-4">Keskintur Portal</div>
      </div>
    </div>
  );
}
